import fetchApi from '@/utils/fetchApi'
import { useConfigurator } from '@/composables/configurator/useConfigurator'
import type { DispatchMetadata, AvailableVoices } from 'shared/src'

export interface PhoneRoutingConfig {
  default_config_id: string | null
  allowed_config_ids: string[]
}

const errorLoading = ref(false)
const isConfigurationLoaded = ref(false)

export const useConfiguratorApi = () => {
  const {
    setConfigurationId,
    setGeneralInformation,
    setConversationFlow,
    setCustomerIntents,
    setInactivity,
    setCallDurationLimits,
    setKnowledge,
    setExperimental,
    setExternalTools,
    payload,
    saveInitialConfiguration,
    setEnabled,
  } = useConfigurator()

  const getConfiguration = async (id: string, duplicate: boolean) => {
    if (id) setConfigurationId(id)

    try {
      errorLoading.value = false
      isConfigurationLoaded.value = false

      const jsonResponse = await fetchApi<{ data: DispatchMetadata }>({
        endpoint: `/api/configurations/get/${id}`,
      })

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (jsonResponse.data) {
        const data = jsonResponse.data

        const gi = data.settings.general_information
        const commonTools: string[] = data.tools.common ?? []
        const customSkillActive = !['answer_with_rag', 'qualify_call', 'step_validation'].some(
          (t) => commonTools.includes(t),
        )
        const giForLoad = customSkillActive ? { ...gi, agent_description: '' } : gi
        setGeneralInformation(
          duplicate ? { ...giForLoad, agent_name: giForLoad.agent_name + ' - Copy' } : giForLoad,
        )

        setConversationFlow({
          ...data.settings.conversational_flow,
          opening_phrases: data.scenarios[0]?.opening ?? [],
          closing_phrases: data.scenarios[0]?.closing ?? [],
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          replacements: data.settings.speech?.replacements ?? undefined,
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setCustomerIntents(data.scenarios[0]?.metadata?.customer_intents ?? [])
        if (data.settings.inactivity) {
          setInactivity(data.settings.inactivity)
        }
        // ponytail: legacy `settings.fallback` merged into call_duration_limits for old configs; drop branch once backend stops sending fallback
        const legacyFallback = (
          data.settings as unknown as {
            fallback?: { max_call_duration_output?: string; max_concurrent_calls?: number }
          }
        ).fallback
        const callDurationLimits = data.settings.call_duration_limits as
          typeof data.settings.call_duration_limits | undefined
        if (callDurationLimits || legacyFallback) {
          setCallDurationLimits({
            max_call_duration_seconds: callDurationLimits?.max_call_duration_seconds ?? 120,
            max_call_duration_speech: callDurationLimits?.max_call_duration_speech,
            max_call_duration_output:
              callDurationLimits?.max_call_duration_output ??
              legacyFallback?.max_call_duration_output ??
              'MAX_DURATION',
            max_concurrent_calls:
              callDurationLimits?.max_concurrent_calls ?? legacyFallback?.max_concurrent_calls,
          })
        }
        if (data.settings.knowledge) {
          setKnowledge(data.settings.knowledge)
        }
        // Backend sends experimental data at the root of settings, not in settings.experimental
        // form_schema is stored in settings.form_schema (fallback: scenario.variables for old configs)
        const scenario0 = data.scenarios[0]
        const entryPointPersona = scenario0.entry_point_persona
        const personaData = entryPointPersona ? scenario0.personas[entryPointPersona] : undefined
        setExperimental({
          form_schema: (data.settings.form_schema ?? data.scenario?.variables)?.map(
            (f: { name: string; description: string; type: string }) => ({
              name: f.name,
              description: f.description,
              type: f.type as 'string' | 'int' | 'float' | 'boolean',
            }),
          ),
          fsm: data.scenario?.fsm,
          sampling: data.settings.sampling
            ? {
                ...data.settings.sampling,
                temperature: data.settings.sampling.temperature ?? 0,
                top_k: data.settings.sampling.top_k ?? 0,
                top_p: data.settings.sampling.top_p ?? 0,
              }
            : undefined,
          flags: data.settings.flags,
          tools: { ...data.tools, common: data.tools.common ?? [] },
          features: data.settings.features,
          contextual_prompt_override: data.settings.contextual_prompt_override,
          _agentDescription: gi.agent_description,
          _speech: data.settings.speech,
          additional_parameters: data.settings.additional_parameters,
          vad: data.settings.vad,
          class_identifier: data.scenario?.class_identifier,
          persona_uuid_override: entryPointPersona,
          persona_role_override: personaData?.role,
          persona_run_tools_on_enter: personaData?.run_tools_on_enter,
          persona_voice_ids_override: personaData?.voice_ids,
        })
        setExternalTools(data.settings.external_tools)
        setEnabled(data.settings.enabled)

        isConfigurationLoaded.value = true
        return data
      } else {
        console.warn('ID not found in response body')
        isConfigurationLoaded.value = true
      }
    } catch (e) {
      console.warn('Configuration ID not found, creating new configuration', e)
      setConfigurationId(null)
      isConfigurationLoaded.value = false
      errorLoading.value = true
      return null
    } finally {
      saveInitialConfiguration(payload.value)
    }
  }

  const getConfigurationData = async (id: string): Promise<DispatchMetadata | null> => {
    if (!id) return null

    try {
      const response = await fetchApi<{ data: DispatchMetadata }>({
        endpoint: `/api/configurations/get/${encodeURIComponent(id)}`,
      })
      return response.data
    } catch (e) {
      console.warn('Error while fetching configuration for export', e)
      return null
    }
  }

  const getConfigurationNames = async (): Promise<Record<string, string>> => {
    try {
      const result = await fetchApi<{ configs: Record<string, string> }>({
        endpoint: '/api/configurations/names',
      })
      return result.configs
    } catch (e) {
      console.warn('Error while fetching configuration names', e)
      return {}
    }
  }

  const getAvailableVoices = async (): Promise<AvailableVoices> => {
    try {
      const result = await fetchApi<AvailableVoices>({
        endpoint: '/api/configurations/available-voices',
      })
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return result ?? {}
    } catch (e) {
      console.warn('Error while fetching available voices', e)
      return {}
    }
  }

  const getRoutingConfigs = async (): Promise<Record<string, PhoneRoutingConfig>> => {
    try {
      const result = await fetchApi<{ routing_configs: Record<string, PhoneRoutingConfig> }>({
        endpoint: '/api/configurations/routing-configs',
      })
      return result.routing_configs
    } catch (e) {
      console.error('Failed to fetch routing configs:', e)
      return {}
    }
  }

  const updateRoutingConfig = async (
    trunkId: string,
    defaultConfigId: string | null,
    allowedConfigIds: string[],
  ): Promise<boolean> => {
    try {
      await fetchApi({
        endpoint: `/api/configurations/routing-configs/${encodeURIComponent(trunkId)}`,
        method: 'PUT',
        body: { default_config_id: defaultConfigId, allowed_config_ids: allowedConfigIds },
      })
      return true
    } catch (e) {
      console.error('Failed to update routing config:', e)
      return false
    }
  }

  return {
    getConfiguration,
    errorLoading,
    isConfigurationLoaded,
    getConfigurationNames,
    getAvailableVoices,
    getConfigurationData,
    getRoutingConfigs,
    updateRoutingConfig,
  }
}
